import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { apiLinks } from '../../core/api-links/api-links';
import { AuthService } from '../auth.service';

@Injectable({ providedIn: 'root' })
export class CommHubService {
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private authHeaders(extra?: Record<string, string>): HttpHeaders | undefined {
    const token = this.authService.getUser?.accessToken;
    if (!token) {
      return extra ? new HttpHeaders(extra) : undefined;
    }
    return new HttpHeaders({ xaccesstoken: token, ...(extra || {}) });
  }

  private unwrap<T>(obs: Observable<any>): Observable<T> {
    return obs.pipe(map((res) => (res?.data !== undefined ? res.data : res)));
  }

  loadNotifications(options?: {
    category?: string;
    cursor?: string;
    limit?: number;
  }): Observable<{ items: any[]; unreadCount: number; nextCursor?: string | null }> {
    let params = new HttpParams();
    if (options?.category) {
      params = params.set('category', options.category);
    }
    if (options?.cursor) {
      params = params.set('cursor', options.cursor);
    }
    if (options?.limit) {
      params = params.set('limit', String(options.limit));
    }
    return this.unwrap(
      this.http.get(`${apiLinks.commHub.base}/notifications`, {
        headers: this.authHeaders(),
        params
      })
    );
  }

  getUnreadCount(): Observable<{ count: number }> {
    return this.unwrap(
      this.http.get(`${apiLinks.commHub.base}/notifications/unread-count`, {
        headers: this.authHeaders()
      })
    );
  }

  markNotificationRead(id: string): Observable<any> {
    return this.unwrap(
      this.http.patch(`${apiLinks.commHub.base}/notifications/${id}/read`, {}, {
        headers: this.authHeaders()
      })
    );
  }

  markAllNotificationsRead(category?: string): Observable<{ marked: number }> {
    let params = new HttpParams();
    if (category) {
      params = params.set('category', category);
    }
    return this.unwrap(
      this.http.patch(`${apiLinks.commHub.base}/notifications/read-all`, {}, {
        headers: this.authHeaders(),
        params
      })
    );
  }

  pinNotification(id: string, pinned = true): Observable<any> {
    return this.unwrap(
      this.http.patch(`${apiLinks.commHub.base}/notifications/${id}/pin`, { pinned }, {
        headers: this.authHeaders()
      })
    );
  }

  /** REST — file upload (broadcast attachments) */
  uploadFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.unwrap(this.http.post(apiLinks.commUpload, formData, {
      headers: this.authHeaders()
    }));
  }

  /** REST — delete a previously uploaded broadcast attachment by URL */
  deleteFile(url: string): Observable<any> {
    return this.unwrap(this.http.delete(apiLinks.commUpload, {
      headers: this.authHeaders(),
      body: { url }
    }));
  }

  sendBroadcast(
    payload: {
      title: string;
      body: string;
      category?: string;
      audience?: {
        everyone?: boolean;
        roles?: string[];
        emails?: string[];
        tpns?: string[];
      };
      attachmentUrl?: string;
      attachmentName?: string;
      attachmentType?: string;
    },
    idempotencyKey?: string
  ): Observable<any> {
    const headers = this.authHeaders(
      idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
    );
    return this.unwrap(
      this.http.post(`${apiLinks.commHub.base}/broadcasts`, { ...payload, sendNow: true }, {
        headers
      })
    );
  }
}
